// API bindings: sources (Auto generated)

"use strict";

import { RequestErrorHandler, RequestParams, CommonAuthenticatedErrorHandler } from "@asanrom/request-browser";
import { getApiUrl, generateURIQuery } from "./utils";
import { DataSourceConnectionType, DataSourceList, CreateSourceResponse, CreateSourceBody, DataSourceFullInformation, DataSourceDataApiDetails, NamedReference, RenameDataSourceBody, ModifyFieldsDataSourceBody, ModifySourceConfigResponse, ModifyConfigDataSourceBody, DataSourceTestConnectivityResponse, DataSourceGetFieldsResponse, DataSourceExistsResponse } from "./definitions";

export class ApiSources {
    /**
     * Method: GET
     * Path: /source-types
     * Gets list of data source connection types
     * Requires permission: sources.read or pipelines.write
     * @param queryParams Query parameters
     * @returns The request parameters
     */
    public static ListSourceTypes(queryParams: ListSourceTypesQueryParameters): RequestParams<DataSourceConnectionType[], ListSourceTypesErrorHandler> {
        return {
            method: "GET",
            url: getApiUrl(`/source-types` + generateURIQuery(queryParams)),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(403, "*", handler.forbidden)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: GET
     * Path: /sources
     * Gets list of data sources
     * Requires permission: sources.read
     * @param queryParams Query parameters
     * @returns The request parameters
     */
    public static ListSources(queryParams: ListSourcesQueryParameters): RequestParams<DataSourceList, ListSourcesErrorHandler> {
        return {
            method: "GET",
            url: getApiUrl(`/sources` + generateURIQuery(queryParams)),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(403, "*", handler.forbidden)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: POST
     * Path: /sources
     * Creates new data source
     * Requires permission: sources.write
     * @param body Body parameters
     * @returns The request parameters
     */
    public static CreateSource(body: CreateSourceBody): RequestParams<CreateSourceResponse, CreateSourceErrorHandler> {
        return {
            method: "POST",
            url: getApiUrl(`/sources`),
            json: body,
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(403, "*", handler.forbidden)
                    .add(400, "INVALID_FIELDS", handler.badRequestInvalidFields)
                    .add(400, "INVALID_CONNECTION_CONFIG", handler.badRequestInvalidConnectionConfig)
                    .add(400, "INVALID_CONNECTION_TYPE", handler.badRequestInvalidConnectionType)
                    .add(400, "INVALID_DATA_FLOW_TYPE", handler.badRequestInvalidDataFlowType)
                    .add(400, "INVALID_NAME", handler.badRequestInvalidName)
                    .add(400, "*", handler.badRequest)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: GET
     * Path: /sources/{id}
     * Gets source information
     * Requires permission: sources.read
     * @param id Data source ID
     * @returns The request parameters
     */
    public static GetSource(id: string): RequestParams<DataSourceFullInformation, GetSourceErrorHandler> {
        return {
            method: "GET",
            url: getApiUrl(`/sources/${encodeURIComponent(id)}`),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(404, "*", handler.notFound)
                    .add(403, "*", handler.forbidden)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: DELETE
     * Path: /sources/{id}
     * Deletes data source
     * Requires permission: sources.write
     * @param id Data source ID
     * @returns The request parameters
     */
    public static DeleteSource(id: string): RequestParams<void, DeleteSourceErrorHandler> {
        return {
            method: "DELETE",
            url: getApiUrl(`/sources/${encodeURIComponent(id)}`),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(403, "*", handler.forbidden)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: GET
     * Path: /sources/{id}/data
     * Gets source data API URL and authentication details
     * Requires permission: sources.write
     * @param id Data source ID
     * @returns The request parameters
     */
    public static GetSourceDataApi(id: string): RequestParams<DataSourceDataApiDetails, GetSourceDataApiErrorHandler> {
        return {
            method: "GET",
            url: getApiUrl(`/sources/${encodeURIComponent(id)}/data`),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(404, "*", handler.notFound)
                    .add(403, "*", handler.forbidden)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: GET
     * Path: /sources/{id}/datasets
     * Gets linked datasets to the source
     * Requires permission: sources.read
     * @param id Data source ID
     * @returns The request parameters
     */
    public static GetSourceLinkedDatasets(id: string): RequestParams<NamedReference[], GetSourceLinkedDatasetsErrorHandler> {
        return {
            method: "GET",
            url: getApiUrl(`/sources/${encodeURIComponent(id)}/datasets`),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(404, "*", handler.notFound)
                    .add(403, "*", handler.forbidden)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: POST
     * Path: /sources/{id}/name
     * Renames data source
     * Requires permission: sources.write
     * @param id Data source ID
     * @param body Body parameters
     * @returns The request parameters
     */
    public static RenameSource(id: string, body: RenameDataSourceBody): RequestParams<void, RenameSourceErrorHandler> {
        return {
            method: "POST",
            url: getApiUrl(`/sources/${encodeURIComponent(id)}/name`),
            json: body,
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(404, "*", handler.notFound)
                    .add(403, "*", handler.forbidden)
                    .add(400, "INVALID_NAME", handler.badRequestInvalidName)
                    .add(400, "*", handler.badRequest)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: POST
     * Path: /sources/{id}/fields
     * Modifies fields of data source
     * Requires permission: sources.write
     * @param id Data source ID
     * @param body Body parameters
     * @returns The request parameters
     */
    public static ModifySourceFields(id: string, body: ModifyFieldsDataSourceBody): RequestParams<void, ModifySourceFieldsErrorHandler> {
        return {
            method: "POST",
            url: getApiUrl(`/sources/${encodeURIComponent(id)}/fields`),
            json: body,
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(404, "*", handler.notFound)
                    .add(403, "*", handler.forbidden)
                    .add(400, "INVALID_FIELDS", handler.badRequestInvalidFields)
                    .add(400, "*", handler.badRequest)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: POST
     * Path: /sources/{id}/config
     * Modifies data source configuration
     * Requires permission: sources.write
     * @param id Data source ID
     * @param body Body parameters
     * @returns The request parameters
     */
    public static ModifySourceConfig(id: string, body: ModifyConfigDataSourceBody): RequestParams<ModifySourceConfigResponse, ModifySourceConfigErrorHandler> {
        return {
            method: "POST",
            url: getApiUrl(`/sources/${encodeURIComponent(id)}/config`),
            json: body,
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(404, "*", handler.notFound)
                    .add(403, "*", handler.forbidden)
                    .add(400, "INVALID_CONNECTION_CONFIG", handler.badRequestInvalidConnectionConfig)
                    .add(400, "INVALID_CONNECTION_TYPE", handler.badRequestInvalidConnectionType)
                    .add(400, "*", handler.badRequest)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: POST
     * Path: /sources_tools/test
     * Tests connectivity to a data source
     * Requires permission: sources.write
     * @param body Body parameters
     * @returns The request parameters
     */
    public static TestConnectivity(body: ModifyConfigDataSourceBody): RequestParams<DataSourceTestConnectivityResponse, TestConnectivityErrorHandler> {
        return {
            method: "POST",
            url: getApiUrl(`/sources_tools/test`),
            json: body,
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(403, "*", handler.forbidden)
                    .add(400, "INVALID_CONNECTION_CONFIG", handler.badRequestInvalidConnectionConfig)
                    .add(400, "INVALID_CONNECTION_TYPE", handler.badRequestInvalidConnectionType)
                    .add(400, "*", handler.badRequest)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: POST
     * Path: /sources_tools/get_fields
     * Connects to the data source and figures out the list of fields.
     * May return an empty list if the data source cannot be reached.
     * Requires permission: sources.write
     * @param body Body parameters
     * @returns The request parameters
     */
    public static GetFields(body: ModifyConfigDataSourceBody): RequestParams<DataSourceGetFieldsResponse, GetFieldsErrorHandler> {
        return {
            method: "POST",
            url: getApiUrl(`/sources_tools/get_fields`),
            json: body,
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(403, "*", handler.forbidden)
                    .add(400, "INVALID_CONNECTION_CONFIG", handler.badRequestInvalidConnectionConfig)
                    .add(400, "INVALID_CONNECTION_TYPE", handler.badRequestInvalidConnectionType)
                    .add(400, "*", handler.badRequest)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: GET
     * Path: /sources/check-name/{name}
     * Gets source information
     * Requires permission: sources.read
     * @param name Data source name
     * @returns The request parameters
     */
    public static GetSourceByName(name: string): RequestParams<DataSourceExistsResponse, GetSourceByNameErrorHandler> {
        return {
            method: "GET",
            url: getApiUrl(`/sources/check-name/${encodeURIComponent(name)}`),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(403, "*", handler.forbidden)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }
}

/**
 * Error handler for ListSourceTypes
 */
export type ListSourceTypesErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 403
     */
    forbidden: () => void;
};

/**
 * Query parameters for ListSourceTypes
 */
export interface ListSourceTypesQueryParameters {
    /**
     * Language for the documentation fields (name, description, etc.)
     */
    lang?: string;
}

/**
 * Error handler for ListSources
 */
export type ListSourcesErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 403
     */
    forbidden: () => void;
};

/**
 * Query parameters for ListSources
 */
export interface ListSourcesQueryParameters {
    /**
     * Max number of items to fetch per page. Hard limit: 256
     */
    limit?: number;

    /**
     * Continuation token, to request more pages
     */
    continuation?: number;
}

/**
 * Error handler for CreateSource
 */
export type CreateSourceErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 400
     */
    badRequest: () => void;

    /**
     * Invalid name
     */
    badRequestInvalidName: () => void;

    /**
     * Invalid data flow type
     */
    badRequestInvalidDataFlowType: () => void;

    /**
     * Invalid or incompatible connection
     */
    badRequestInvalidConnectionType: () => void;

    /**
     * Invalid connection configuration
     */
    badRequestInvalidConnectionConfig: () => void;

    /**
     * Invalid fields definition
     */
    badRequestInvalidFields: () => void;

    /**
     * General handler for status = 403
     */
    forbidden: () => void;
};

/**
 * Error handler for GetSource
 */
export type GetSourceErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 403
     */
    forbidden: () => void;

    /**
     * General handler for status = 404
     */
    notFound: () => void;
};

/**
 * Error handler for DeleteSource
 */
export type DeleteSourceErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 403
     */
    forbidden: () => void;
};

/**
 * Error handler for GetSourceDataApi
 */
export type GetSourceDataApiErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 403
     */
    forbidden: () => void;

    /**
     * General handler for status = 404
     */
    notFound: () => void;
};

/**
 * Error handler for GetSourceLinkedDatasets
 */
export type GetSourceLinkedDatasetsErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 403
     */
    forbidden: () => void;

    /**
     * General handler for status = 404
     */
    notFound: () => void;
};

/**
 * Error handler for RenameSource
 */
export type RenameSourceErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 400
     */
    badRequest: () => void;

    /**
     * Invalid name
     */
    badRequestInvalidName: () => void;

    /**
     * General handler for status = 403
     */
    forbidden: () => void;

    /**
     * General handler for status = 404
     */
    notFound: () => void;
};

/**
 * Error handler for ModifySourceFields
 */
export type ModifySourceFieldsErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 400
     */
    badRequest: () => void;

    /**
     * Invalid fields definition
     */
    badRequestInvalidFields: () => void;

    /**
     * General handler for status = 403
     */
    forbidden: () => void;

    /**
     * General handler for status = 404
     */
    notFound: () => void;
};

/**
 * Error handler for ModifySourceConfig
 */
export type ModifySourceConfigErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 400
     */
    badRequest: () => void;

    /**
     * Invalid or incompatible connection
     */
    badRequestInvalidConnectionType: () => void;

    /**
     * Invalid connection configuration
     */
    badRequestInvalidConnectionConfig: () => void;

    /**
     * General handler for status = 403
     */
    forbidden: () => void;

    /**
     * General handler for status = 404
     */
    notFound: () => void;
};

/**
 * Error handler for TestConnectivity
 */
export type TestConnectivityErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 400
     */
    badRequest: () => void;

    /**
     * Invalid or incompatible connection
     */
    badRequestInvalidConnectionType: () => void;

    /**
     * Invalid connection configuration
     */
    badRequestInvalidConnectionConfig: () => void;

    /**
     * General handler for status = 403
     */
    forbidden: () => void;
};

/**
 * Error handler for GetFields
 */
export type GetFieldsErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 400
     */
    badRequest: () => void;

    /**
     * Invalid or incompatible connection
     */
    badRequestInvalidConnectionType: () => void;

    /**
     * Invalid connection configuration
     */
    badRequestInvalidConnectionConfig: () => void;

    /**
     * General handler for status = 403
     */
    forbidden: () => void;
};

/**
 * Error handler for GetSourceByName
 */
export type GetSourceByNameErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 403
     */
    forbidden: () => void;
};

